// Only the latest request may write, so a list fetched before a catalogue sweep
// finished cannot land over the one fetched after it.
let latestWorkspacesRequest = 0;

// Interface answers its 4xx as a plain string or a { message } body; the console API passes either on.
const getErrorMessage = (error) => error?.data?.message || (typeof error?.data === 'string' ? error.data : '');

// Interface's refusals of an IČO or a VAT ID, and the words support reads for each.
const TAX_FIELD_REFUSALS = {
  'Invalid VAT ID.': 'workspace.vatId.invalid',
  'Invalid tax number.': 'workspace.taxNumber.invalid',
};

export const useWorkspacesStore = defineStore('workspaces', {
  state: () => {
    return {
      total: 0,
      currentPage: 1,
      perPage: 100,
      searchQuery: '',
      workspaces: [],
      templateCopyWorkspaces: [],
    };
  },

  actions: {
    async fetchWorkspaces() {
      latestWorkspacesRequest += 1;
      const request = latestWorkspacesRequest;

      const response = await useApi.get('/workspaces').catch(() => {
        useToast.error(useT('bcd101'));
      });

      if (response && request === latestWorkspacesRequest) {
        this.total = response.total;
        this.workspaces = response.results;
      }
    },

    async fetchWorkspacesForTemplateCopy() {
      if (this.templateCopyWorkspaces.length) return this.templateCopyWorkspaces;

      const response = await useApi.get('/workspaces', { params: { includeIgnored: 'true' } }).catch(() => {
        useToast.error(useT('bcd101'));
      });

      if (response) {
        this.templateCopyWorkspaces = response.results;
      }

      return this.templateCopyWorkspaces;
    },

    async create(body) {
      try {
        const response = await useApi.post('/workspaces', {
          body,
        });

        return response;
      } catch (error) {
        if (TAX_FIELD_REFUSALS[getErrorMessage(error)]) {
          useToast.error(useT(TAX_FIELD_REFUSALS[getErrorMessage(error)]));
          return;
        }

        if (error.response?.status === 400 || error.response?.status === 409) {
          useToast.error(useT('bcd238'));
          return;
        }

        console.error(error);
        useToast(useT('bcd100'));
      }
    },

    // { currency, requiredWorkspaceFields, taxNumber, vatId }: what interface reads off the
    // workspace's Stripe customer, and the fields support corrects.
    async getTaxSettings(domain) {
      const response = await useApi.get(`/workspaces/${domain}/tax-settings`).catch((error) => {
        console.error(error);
        useToast.error(useT('bcd270'));
      });

      return response;
    },

    async updateTaxSettings(domain, body) {
      try {
        return await useApi.put(`/workspaces/${domain}/tax-settings`, { body });
      } catch (error) {
        console.error(error);
        useToast.error(useT(TAX_FIELD_REFUSALS[getErrorMessage(error)] || 'bcd270'));
      }
    },

    async access(domain) {
      const response = await useApi.post(`/workspaces/${domain}/access`).catch(() => {
        useToast.error(useT('bcd99'));
      });

      if (response) {
        useToast(useT('bcd98'));
        const storage = useSessionStorage(`access-${domain}-validUntil`, '');
        storage.value = response.validUntil;
      }

      return response;
    },

    async getUsersLogs(domain) {
      const response = await useApi.get(`/workspaces/${domain}/users/logs`).catch(() => {
        useToast.error(useT('bcd97'));
      });

      return response;
    },

    async fetchUsers(domain) {
      const response = await useApi.get(`/workspaces/${domain}/users`).catch(() => {
        useToast.error(useT('bcd270'));
      });

      return response || [];
    },
  },

  getters: {
    getWorkspaces(state) {
      return _orderBy(filterSearchInFields({
        items: state.workspaces,
        fields: ['name', 'domain'],
        query: state.searchQuery,
      }) || [], 'name', 'asc').slice(state.perPage * (state.currentPage - 1), state.perPage * state.currentPage);
    },
  },
});
